/**
 * desktopNotify — the notification that reaches you when Karr isn't the tab
 * you're looking at.
 *
 * The bell, the chime and the toast all assume Karr is on screen. Email and
 * WhatsApp don't make that assumption, and neither should we: when the window is
 * hidden or unfocused, a genuinely new notification raises an OS-level one that
 * takes you straight to the right screen.
 *
 * Rules of the road:
 *   • Permission is only ever requested from a real click (browsers reject it
 *     otherwise, and a permission prompt on page load is hostile anyway).
 *   • Off unless the user has BOTH granted permission and not turned it off.
 *   • Never raised while the window is focused — the in-app toast covers that,
 *     and a duplicate OS banner for something you're already looking at is noise.
 *
 * @since 3.4.0
 */

const PREF_KEY = 'karr_notif_desktop';   // 'on' | 'off' — default on once granted
const ICON = '/pwa-192x192.png';

export type DesktopState = 'unsupported' | 'denied' | 'off' | 'ask' | 'on';

function supported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
}

function prefOff(): boolean {
    try { return localStorage.getItem(PREF_KEY) === 'off'; } catch { return false; }
}

/** What the settings toggle should show. */
export function desktopState(): DesktopState {
    if (!supported()) return 'unsupported';
    const perm = Notification.permission;
    if (perm === 'denied') return 'denied';
    if (perm === 'default') return 'ask';
    return prefOff() ? 'off' : 'on';
}

/** True when a new notification should raise an OS banner. */
export function desktopEnabled(): boolean {
    return desktopState() === 'on';
}

/**
 * Turn desktop alerts on or off. Turning them ON asks the browser for permission
 * — call this only from a click handler. Returns the resulting state.
 */
export async function toggleDesktopNotifications(): Promise<DesktopState> {
    if (!supported()) return 'unsupported';
    const state = desktopState();
    if (state === 'on') {
        try { localStorage.setItem(PREF_KEY, 'off'); } catch { /* ignore */ }
        return 'off';
    }
    if (Notification.permission === 'default') {
        let perm: NotificationPermission = 'default';
        try { perm = await Notification.requestPermission(); } catch { /* ignore */ }
        if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'ask';
    }
    if (Notification.permission === 'denied') return 'denied';
    try { localStorage.setItem(PREF_KEY, 'on'); } catch { /* ignore */ }
    return 'on';
}

/** Is Karr the window the user is actually looking at right now? */
export function windowIsForeground(): boolean {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * Raise one OS notification. Clicking it focuses Karr and navigates to `href`.
 * Silent no-op when unsupported, not permitted, turned off, or the window is
 * already in front. Never throws.
 */
export function showDesktopNotification(opts: {
    id: string; title: string; body: string; href?: string;
    /** Instead of `href`: what a click opens without reloading the page (the Karr Phone on a chat). */
    onOpen?: () => void;
}): void {
    try {
        if (!desktopEnabled() || windowIsForeground()) return;
        const n = new Notification(opts.title, {
            body: opts.body,
            icon: ICON,
            badge: ICON,
            // Same id replaces rather than stacks, so a refetch can't double-banner.
            tag: opts.id,
            renotify: false,
        } as NotificationOptions);
        n.onclick = () => {
            try {
                window.focus();
                if (opts.onOpen) opts.onOpen();
                else if (opts.href) window.location.assign(opts.href);
            } catch { /* ignore */ }
            n.close();
        };
    } catch {
        // Android Chrome throws "Illegal constructor" — notifications there must
        // come from the service worker. Best-effort; no click handling available.
        try {
            void navigator.serviceWorker?.ready?.then((reg) => {
                void reg.showNotification(opts.title, {
                    body: opts.body, icon: ICON, badge: ICON, tag: opts.id,
                    data: { href: opts.href },
                });
            });
        } catch { /* genuinely unavailable — the bell still has it */ }
    }
}
